// frontend/src/components/admin/UserManagement.js
// Admin Users and invitations (/admin/users): wireframes wf-admin-users-1 … -7,
// calls in docs/call-map.md section 3 (YES-281). IDs travel only in API paths.
import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Menu,
    MenuItem,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Box,
    Alert,
    Snackbar,
    Select,
    Chip,
    Radio,
    RadioGroup,
    FormControl,
    FormLabel,
    FormControlLabel,
    CircularProgress,
} from '@mui/material';
import { MoreHoriz as MoreIcon } from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// Shown in this order in the invite dialog; `user` is shown as Teacher
const ROLES = [
    { value: 'annotator', label: 'Annotator', help: 'Codes and adjudicates the items assigned to them. Lands on My queue.' },
    { value: 'researcher', label: 'Researcher', help: 'Sets up runs, assigns lessons and exports. Lands on Runs.' },
    { value: 'user', label: 'Teacher', help: 'Uploads and analyses their own lessons. Lands on My lessons.' },
    { value: 'admin', label: 'Admin', help: 'Everything a researcher can do, plus users and activity.' },
];
const roleLabel = (role) => ROLES.find((r) => r.value === role)?.label || role;

const errorOf = (error) => error.response?.data?.error;

const invitedOn = (iso) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const StatusChip = ({ row }) => {
    if (row.status === 'pending') {
        return <Chip size="small" label={`Invited ${invitedOn(row.invitedAt)}`} sx={{ bgcolor: '#fff4d6', color: '#8a5a00' }} />;
    }
    if (row.status === 'no login') {
        return <Chip size="small" label="No sign-in yet" sx={{ bgcolor: 'grey.200', color: 'text.secondary' }} />;
    }
    return <Chip size="small" label="Active" sx={{ bgcolor: '#e6f4ea', color: 'success.dark' }} />;
};

// A role that cannot be changed here: your own, or an invitation's
const FixedRole = ({ role }) => (
    <Box sx={{ width: 160, px: 1.5, py: 0.75, borderRadius: 1, bgcolor: 'grey.100', color: 'text.secondary' }}>
        {roleLabel(role)}
    </Box>
);

const InviteDialog = ({ open, onClose, onSent }) => {
    const [email, setEmail] = useState('');
    const [role, setRole] = useState(''); // no role pre-selected (decision 2026-10-22)
    const [emailError, setEmailError] = useState('');
    const [formError, setFormError] = useState('');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (open) {
            setEmail('');
            setRole('');
            setEmailError('');
            setFormError('');
        }
    }, [open]);

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        setEmailError('');
        setFormError('');
        try {
            const response = await api.post('/api/admin/users', { email, role });
            onSent(response.data.data);
        } catch (error) {
            const status = error.response?.status;
            if (status === 409 || status === 400) {
                setEmailError(errorOf(error) || 'Check the email.');
            } else {
                // 502, no connection: keep the form so it can be sent again
                setFormError(errorOf(error) || "Couldn't send the invitation. Try again.");
            }
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <form onSubmit={submit}>
                <DialogTitle>Invite a user</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        They get an email with a sign-up link. Their role is set when they first sign in.
                    </DialogContentText>
                    {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
                    <TextField
                        label="Email"
                        type="email"
                        required
                        fullWidth
                        autoFocus
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                        error={Boolean(emailError)}
                        helperText={emailError}
                        sx={{ mb: 2 }}
                    />
                    <FormControl fullWidth>
                        <FormLabel sx={{ mb: 1 }}>Role</FormLabel>
                        <RadioGroup value={role} onChange={(e) => setRole(e.target.value)}>
                            {ROLES.map((r) => (
                                <FormControlLabel
                                    key={r.value}
                                    value={r.value}
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            <Typography variant="body1">{r.label}</Typography>
                                            <Typography variant="body2" color="text.secondary">{r.help}</Typography>
                                        </Box>
                                    }
                                    sx={{
                                        mx: 0, mb: 1, py: 0.5, pr: 1,
                                        border: 1, borderRadius: 1,
                                        borderColor: role === r.value ? 'primary.main' : 'divider',
                                        bgcolor: role === r.value ? 'action.selected' : 'transparent',
                                    }}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button variant="outlined" onClick={onClose}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={!email || !role || Boolean(emailError) || sending}>
                        {sending ? 'Sending…' : 'Send invitation'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

// One confirmation dialog for a role change, a revoke and a deletion. The
// request runs here so a refusal (409, 502) shows in the dialog.
const ConfirmDialog = ({ action, onClose, onDone }) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [refused, setRefused] = useState(false);

    useEffect(() => {
        setBusy(false);
        setError('');
        setRefused(false);
    }, [action]);

    if (!action) return null;

    const confirm = async () => {
        setBusy(true);
        setError('');
        try {
            await action.run();
            onDone(action.done);
        } catch (err) {
            setError(errorOf(err) || 'Something went wrong. Try again.');
            // A 409 stays refused until something else changes (frame 6); a 502 can be tried again
            setRefused(err.response?.status === 409);
            setBusy(false);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{action.title}</DialogTitle>
            <DialogContent>
                <DialogContentText>{action.text}</DialogContentText>
                {error && <Alert severity="warning" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant="outlined" onClick={onClose}>Cancel</Button>
                <Button variant="contained" color={action.danger ? 'error' : 'primary'} onClick={confirm} disabled={busy || refused}>
                    {action.confirm}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const UserManagement = () => {
    const { user, can } = useAuth();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [justInvited, setJustInvited] = useState(null);
    const [action, setAction] = useState(null);
    const [menu, setMenu] = useState(null); // { anchor, row }
    const [toast, setToast] = useState('');

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const response = await api.get('/api/admin/users');
            setRows(response.data.data);
        } catch (error) {
            // Never a partial or empty list in place of the error (frame 7)
            setRows([]);
            setLoadError(error.response?.status || 'network');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const done = (message) => {
        setAction(null);
        setToast(message);
        fetchUsers();
    };

    const invitationSent = (invitation) => {
        setInviteOpen(false);
        setJustInvited(invitation.id);
        done(`Invitation sent to ${invitation.email}`);
    };

    const askRoleChange = (row, role) => setAction({
        title: `Change ${row.username}'s role?`,
        text: `${roleLabel(row.role)} → ${roleLabel(role)}. Their pages change the next time ADAS loads for them.`,
        confirm: 'Change role',
        run: () => api.put(`/api/admin/users/${row._id}`, { role }),
        done: `${row.username} is now ${roleLabel(role)}`,
    });

    const askRevoke = (row) => setAction({
        title: 'Revoke the invitation?',
        text: `The link sent to ${row.email} stops working. You can invite them again later.`,
        confirm: 'Revoke',
        danger: true,
        run: () => api.delete(`/api/admin/invitations/${row._id}`),
        done: `Invitation to ${row.email} revoked`,
    });

    const askDelete = (row) => setAction({
        title: `Delete ${row.username}'s account?`,
        text: `The account and every lesson and analysis ${row.email} uploaded are deleted. This cannot be undone.`,
        confirm: 'Delete account',
        danger: true,
        run: () => api.delete(`/api/admin/users/${row._id}`),
        done: `${row.username}'s account deleted`,
    });

    // The invitation just sent comes first, highlighted (frame 4)
    const ordered = justInvited
        ? [...rows.filter((r) => r._id === justInvited), ...rows.filter((r) => r._id !== justInvited)]
        : rows;

    const renderBody = () => {
        if (loading) {
            return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
        }
        if (loadError === 403) {
            return <Alert severity="warning" sx={{ m: 3 }}>You do not have access to this page.</Alert>;
        }
        if (loadError) {
            return (
                <Box sx={{ p: 4 }}>
                    <Typography variant="h6" gutterBottom>Couldn't load users</Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                        {loadError === 502
                            ? "The sign-in service didn't answer, so the list could be missing people and invitations. Nothing was changed."
                            : "ADAS didn't answer. Nothing was changed."}
                    </Typography>
                    <Button variant="contained" onClick={fetchUsers}>Try again</Button>
                </Box>
            );
        }
        return (
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            {['Name', 'Email', 'Role', 'Status', ''].map((h) => (
                                <TableCell key={h} sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {ordered.map((row) => {
                            const pending = row.status === 'pending';
                            const you = row._id === user?.id;
                            return (
                                <TableRow key={row._id} sx={row._id === justInvited ? { bgcolor: '#e6f4ea' } : undefined}>
                                    <TableCell sx={{ fontWeight: 500 }}>
                                        {pending ? '—' : row.username}{you && '  (you)'}
                                    </TableCell>
                                    <TableCell>{row.email}</TableCell>
                                    <TableCell>
                                        {pending || you ? (
                                            <FixedRole role={row.role} />
                                        ) : (
                                            <>
                                                <Select
                                                    size="small"
                                                    value={row.role}
                                                    onChange={(e) => askRoleChange(row, e.target.value)}
                                                    sx={{ width: 160 }}
                                                    SelectDisplayProps={{ 'aria-label': `Role of ${row.username}` }}
                                                >
                                                    {ROLES.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                                                </Select>
                                                {row.clerkRole !== undefined && (
                                                    <Typography variant="caption" color="warning.dark" display="block">
                                                        Edited in the sign-in service ({row.clerkRole || 'none'}); ADAS uses this role
                                                    </Typography>
                                                )}
                                            </>
                                        )}
                                    </TableCell>
                                    <TableCell><StatusChip row={row} /></TableCell>
                                    <TableCell align="right">
                                        {pending && <Button variant="outlined" onClick={() => askRevoke(row)}>Revoke</Button>}
                                        {!pending && !you && can('data.purge') && (
                                            <IconButton aria-label={`More for ${row.username}`} onClick={(e) => setMenu({ anchor: e.currentTarget, row })}>
                                                <MoreIcon />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>Users</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Invite people and set their role. Roles change only here, never in the sign-in service.
                    </Typography>
                </Box>
                <Button variant="contained" onClick={() => setInviteOpen(true)}>Invite user</Button>
            </Box>

            <Paper variant="outlined">{renderBody()}</Paper>

            <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)}>
                <MenuItem sx={{ color: 'error.main' }} onClick={() => { askDelete(menu.row); setMenu(null); }}>
                    Delete account
                </MenuItem>
            </Menu>

            <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} onSent={invitationSent} />
            <ConfirmDialog action={action} onClose={() => setAction(null)} onDone={done} />

            <Snackbar
                open={Boolean(toast)}
                autoHideDuration={6000}
                onClose={() => setToast('')}
                message={toast}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Container>
    );
};

export default UserManagement;
